<script setup lang="ts">
import { computed, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import { useGetFieldParts } from "@/stores/queries/field-observations.query";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";

/**
 * Barre basse du MODE ASSOCIATION (onglet Parties du volet Observations) :
 * elle prend la place de la barre de sélection pendant qu'une partie est
 * sélectionnée — consigne, zone de sélection à armer (bouton plein, c'est
 * l'action de la barre ; vert une fois armée, comme le trait à tracer), et
 * une croix pour la fermer. Bleue comme les observations qu'elle classe.
 */

const props = defineProps<{
  diagnosticId: number;
}>();

const { t } = useTranslation();
const state = usePlanAnalysisState();
const { data: parts } = useGetFieldParts(toRef(props, "diagnosticId"));

const partName = computed(
  () => parts.value?.find((p) => p.id === state.assignPartId)?.name ?? "",
);
</script>

<template>
  <div class="assign-bar canvas-bottom-bar text-white d-flex align-items-center gap-3 px-3 py-2">
    <PhosphorIcon name="crosshair" :size="18" weight="bold" class="flex-shrink-0" />
    <span class="assign-text">
      {{ t("canvas:observations.parts.assignHint", { part: partName }) }}
    </span>
    <DRBtn
      icon="bi-bounding-box"
      :label="
        state.zoneSelectMode
          ? t('canvas:observations.parts.zoneActive')
          : t('canvas:observations.parts.zone')
      "
      :variant="state.zoneSelectMode ? 'success' : 'primary'"
      size="sm"
      @click="state.zoneSelectMode = !state.zoneSelectMode"
    />
    <DRBtnIcon
      class="flex-shrink-0"
      variant="flat-white"
      :size="28"
      phosphor-icon="x"
      phosphor-weight="bold"
      :aria-label="t('close')"
      @click="state.setAssignPart(null)"
    />
  </div>
</template>

<style lang="scss" scoped>
.assign-bar {
  border: 1px solid var(--bs-primary);
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  background:
    linear-gradient(
      color-mix(in srgb, var(--bs-primary) 22%, transparent),
      color-mix(in srgb, var(--bs-primary) 22%, transparent)
    ),
    var(--canvas-bg);
  box-shadow:
    0 0 0 1px var(--bs-primary),
    0 -2px 20px color-mix(in srgb, var(--bs-primary) 45%, transparent);
  color: color-mix(in srgb, var(--bs-primary) 45%, white);
  max-width: min(92vw, 720px);
  animation: canvas-bottom-bar-rise 0.18s ease-out;
}

.assign-text {
  font-size: 0.82rem;
  line-height: 1.3;
}

@keyframes canvas-bottom-bar-rise {
  from {
    transform: translateY(100%);
    opacity: 0.4;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
